import type { Ref } from '@vue/composition-api';
import { ref } from '@vue/composition-api';
import { defineStore } from 'pinia';
import type { Resource } from '@getaccept/lib-shared/src/resources/types/resource';
import { useToast } from '@getaccept/lib-shared/src/toast';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { LocalStorageHelper } from '@getaccept/lib-shared/src/helpers/local-storage.helper';
import { LocalStorageKey } from '@getaccept/lib-shared/src/enums/local-storage';
import { EnumDocumentType } from '@getaccept/lib-shared/src/enums/DocumentType';
import { TemplateService } from '@getaccept/editor-lib';
import type { Document } from '@getaccept/lib-shared/src/types/Document';
import { TemplateGalleryService } from '../../api/template-gallery/services/template-gallery.service';
import type { GlobalTemplate, GlobalTemplateImportPayload } from '../types/global-template';
import { ResourceService } from '../../api/resources/services/resource.service';
import { DocumentService } from '../../api/documents/services/document.service';
import { TemplateGalleryConstants } from '../constants/template-gallery.constants';

export const useTemplateGalleryStore = defineStore('templateGallery', () => {
  const loaded = ref(false);
  const loading = ref(false);
  const error = ref(null);
  const { toast } = useToast();

  const loadGlobalResources = async () => {
    loading.value = true;
    loaded.value = false;

    try {
      const response: Resource[] = await TemplateGalleryService.loadGlobalResources(
        TemplateGalleryConstants.defaultFilter
      );
      load(response);
    } catch {
      loaded.value = false;
      loading.value = false;
      toast.danger(t('could-not-load-template-gallery-templates'));
    } finally {
      loading.value = false;
    }
  };

  const templates = ref([]);

  const load = async (globalResources: Resource[]) => {
    loading.value = true;
    loaded.value = false;
    try {
      const mappedGlobalTemplates: GlobalTemplate[] = await Promise.all(
        globalResources.map(
          async (resource: Resource): Promise<GlobalTemplate> =>
            TemplateGalleryService.loadTemplate(resource.contentId)
        )
      );

      templates.value = mappedGlobalTemplates;
      loaded.value = true;
    } catch (e) {
      loading.value = false;
      loaded.value = false;
      toast.danger(t('could-not-load-template-gallery-templates'));
    } finally {
      loading.value = false;
    }
  };

  const documentId = ref('');

  const sendOut = async (templateId: string) => {
    loaded.value = false;
    loading.value = true;
    try {
      const docId: string = await TemplateGalleryService.sendOutTemplate(templateId);
      loaded.value = true;
      documentId.value = docId;
    } catch (e) {
      loaded.value = false;
      loading.value = false;
      toast.danger(t('could-not-send-out-global-template'));
    } finally {
      loading.value = false;
    }
  };

  const newTemplateId = ref('');

  const copy = async (id: string) => {
    loaded.value = false;
    loading.value = true;
    try {
      const templateId: string = await TemplateGalleryService.copyTemplate(id);
      setTimeout(() => {
        loaded.value = true;
        newTemplateId.value = templateId;
        toast.success(t('template-added-to-library'));
      }, 3000);
    } catch (e) {
      loaded.value = false;
      loading.value = false;
      error.value = e;
      LocalStorageHelper.reset(LocalStorageKey.Template);
      toast.danger(t('could-not-copy-global-template'));
    } finally {
      loading.value = false;
    }
  };

  const updateNewTemplateId = (id: string) => {
    newTemplateId.value = id;
  };

  const importLoading = ref(false);
  const importLoaded = ref(false);
  const importError = ref(null);
  const templateResource: Ref<Resource> = ref(null);

  const importGlobalTemplate = async ({
    templateId,
    documentType,
    documentId,
  }: GlobalTemplateImportPayload) => {
    importLoading.value = false;
    importLoading.value = true;
    try {
      const newResourceId: string = await TemplateGalleryService.copyTemplate(templateId);
      const resource: Resource = await ResourceService.loadDetails(newResourceId);

      if (documentType === EnumDocumentType.Template) {
        await ResourceService.implementInDocument({
          documentId,
          resourceId: resource.id,
          contentId: resource.contentId,
        });
        const block = await DocumentService.loadTemplateResourceDetails(resource.contentId);
        const addedResource = { ...resource, block } as Resource;
        importLoaded.value = true;
        templateResource.value = addedResource;
        return;
      }
      importLoaded.value = true;
      templateResource.value = resource;
    } catch (e) {
      importLoading.value = false;
      importLoaded.value = false;
      importError.value = e;
      toast.danger(t('could-not-import-template-gallery-template'));
    } finally {
      importLoading.value = false;
    }
  };

  const resetGlobalTemplateImport = async () => {
    try {
      if (templateResource.value?.id) {
        await ResourceService.delete(templateResource.value.id);
      }
    } catch (e) {
      console.error(e);
    }
    templateResource.value = null;
    importLoading.value = false;
    importLoaded.value = false;
    importError.value = null;
  };

  const previewLoading = ref(false);
  const previewLoaded = ref(false);
  const previewTemplate: Ref<Document> = ref(null);
  const previewError = ref(null);

  const fetchPreview = async (id: string) => {
    previewLoading.value = true;
    try {
      const document: Document = await TemplateService.fetch(id);
      previewLoaded.value = true;
      previewTemplate.value = document;
    } catch (error) {
      previewLoaded.value = false;
      previewLoading.value = false;
      previewError.value = error;
      toast.danger(t('could-not-fetch-template-gallery-preview'));
    } finally {
      previewLoading.value = false;
    }
  };

  const resetPreview = () => {
    previewLoading.value = false;
    previewLoaded.value = false;
    previewTemplate.value = null;
    previewError.value = null;
  };

  return {
    loading,
    loaded,
    templates,
    documentId,
    newTemplateId,
    error,
    templateResource,
    importLoading,
    importLoaded,
    importError,
    previewLoading,
    previewLoaded,
    previewTemplate,
    previewError,
    loadGlobalResources,
    load,
    sendOut,
    copy,
    updateNewTemplateId,
    importGlobalTemplate,
    resetGlobalTemplateImport,
    fetchPreview,
    resetPreview,
  };
});
